package com.speedsoftware.nibbler.ui.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.AcceptInviteRequest
import com.speedsoftware.nibbler.models.InviteDto
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.Screen
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

@Composable
fun AcceptInviteScreen(appState: AppState, token: String) {
    var invite by remember { mutableStateOf<InviteDto?>(null) }
    var name by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(token) {
        val result = appState.apiClient.getInvite(token)
        isLoading = false
        result.onSuccess {
            invite = it
        }.onFailure {
            errorMessage = "Invite is invalid or has expired."
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(NibblerColors.DarkBackground)
            .padding(16.dp),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .background(NibblerColors.DarkSurfaceContainer)
                .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
        ) {
            Column(
                modifier = Modifier.padding(28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // Bauhaus Header
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(32.dp)
                            .background(NibblerColors.BauhausYellow),
                        contentAlignment = Alignment.Center
                    ) {
                        Text("N", color = Color.White, fontSize = 18.sp, fontWeight = FontWeight.Bold)
                    }
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text(
                            text = "Accept your invitation",
                            fontFamily = FontFamily.SansSerif,
                            fontWeight = FontWeight.Bold,
                            fontSize = 20.sp,
                            color = NibblerColors.DarkOnSurface
                        )
                        Text(
                            text = "Create your account to join the team",
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold,
                            color = NibblerColors.DarkOnSurfaceVariant,
                            letterSpacing = 0.08.sp
                        )
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
                Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.DarkOutlineThin))
                Spacer(modifier = Modifier.height(20.dp))

                if (invite != null) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(NibblerColors.DarkSurfaceContainerHighest)
                            .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                            .padding(12.dp)
                    ) {
                        Column {
                            Text(
                                text = "Invited email",
                                fontFamily = FontFamily.SansSerif,
                                fontSize = 10.sp,
                                fontWeight = FontWeight.Bold,
                                color = NibblerColors.DarkOnSurfaceVariant
                            )
                            Text(
                                text = "${invite?.email} (${invite?.role?.name})",
                                fontFamily = FontFamily.SansSerif,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Bold,
                                color = NibblerColors.BauhausBlueLight
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    OutlinedTextField(
                        value = name,
                        onValueChange = { name = it; errorMessage = null },
                        label = { Text("Full name", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    OutlinedTextField(
                        value = password,
                        onValueChange = { password = it; errorMessage = null },
                        label = { Text("Choose a password", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        visualTransformation = PasswordVisualTransformation(),
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(modifier = Modifier.height(16.dp))

                    OutlinedTextField(
                        value = confirmPassword,
                        onValueChange = { confirmPassword = it; errorMessage = null },
                        label = { Text("Confirm password", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                        visualTransformation = PasswordVisualTransformation(),
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                if (errorMessage != null) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Text(
                        text = errorMessage ?: "",
                        color = NibblerColors.Error,
                        fontFamily = FontFamily.SansSerif,
                        fontSize = 11.sp
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                if (invite != null) {
                    Button(
                        onClick = {
                            if (name.isBlank() || password.isBlank()) {
                                errorMessage = "All fields are required."
                                return@Button
                            }
                            if (password != confirmPassword) {
                                errorMessage = "Passwords do not match."
                                return@Button
                            }
                            isLoading = true
                            errorMessage = null
                            scope.launch {
                                val result = appState.apiClient.acceptInvite(
                                    AcceptInviteRequest(
                                        token = token,
                                        name = name,
                                        password = password
                                    )
                                )
                                isLoading = false
                                result.onSuccess { authResponse ->
                                    appState.onAuthenticated(authResponse.user)
                                    appState.navigateTo(Screen.ProjectsList)
                                }.onFailure { error ->
                                    errorMessage = error.message ?: "Failed to accept invite."
                                }
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = NibblerColors.BauhausCoral,
                            contentColor = Color.White
                        ),
                        enabled = !isLoading,
                        modifier = Modifier.fillMaxWidth().height(48.dp)
                    ) {
                        if (isLoading) {
                            CircularProgressIndicator(color = Color.White, strokeWidth = 2.dp, modifier = Modifier.size(20.dp))
                        } else {
                            Text(
                                "Create account and join",
                                fontFamily = FontFamily.SansSerif,
                                fontWeight = FontWeight.Bold,
                                fontSize = 12.sp,
                                letterSpacing = 0.05.sp
                            )
                        }
                    }
                } else if (!isLoading) {
                    Button(
                        onClick = { appState.navigateTo(Screen.Login) },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = NibblerColors.BauhausYellow,
                            contentColor = Color.White
                        ),
                        modifier = Modifier.fillMaxWidth().height(44.dp)
                    ) {
                        Text("← BACK TO LOGIN", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                    }
                }
            }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
fun AcceptInviteScreenPreview() {
    AcceptInviteScreen(appState = AppState(), token = "sample-token")
}
